import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Play, Film } from 'lucide-react';

const MobileMockups = () => {
  const [activeDemo, setActiveDemo] = useState('versatility');

  return (
    <div className="min-h-screen bg-[#0a0a0a] py-12 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-black text-white mb-4">
            Mobile Optimization <span className="text-[#FF6B6B]">Mockups</span>
          </h1>
          <p className="text-gray-400">Critical Changes for Mobile Experience</p>
        </div>

        {/* Demo Selector */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          <button
            onClick={() => setActiveDemo('versatility')}
            className={`px-5 py-2 rounded-full font-medium text-sm transition-all ${
              activeDemo === 'versatility'
                ? 'bg-[#FF6B6B] text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            1. Versatility Section
          </button>
          <button
            onClick={() => setActiveDemo('portraits')}
            className={`px-5 py-2 rounded-full font-medium text-sm transition-all ${
              activeDemo === 'portraits'
                ? 'bg-[#FF6B6B] text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            2. Portrait Gallery
          </button>
          <button
            onClick={() => setActiveDemo('cta')}
            className={`px-5 py-2 rounded-full font-medium text-sm transition-all ${
              activeDemo === 'cta'
                ? 'bg-[#FF6B6B] text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            3. CTA Buttons
          </button>
        </div>

        {/* Demo 1: Versatility Section */}
        {activeDemo === 'versatility' && (
          <div className="space-y-12">
            <h2 className="text-3xl font-bold text-white text-center mb-8">
              Versatility Section - Mobile View
            </h2>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* BEFORE */}
              <div>
                <div className="bg-red-900/20 border-2 border-red-500 rounded-lg p-4 mb-4">
                  <h3 className="text-xl font-bold text-red-400 mb-2">❌ CURRENT (Desktop Layout)</h3>
                  <p className="text-sm text-gray-400">5 cards in one row - breaks on mobile</p>
                </div>
                
                {/* Mobile Frame */}
                <div className="bg-gray-900 rounded-3xl p-4 mx-auto" style={{ maxWidth: '375px' }}>
                  <div className="bg-[#0a0a0a] rounded-2xl overflow-hidden h-[600px] overflow-y-auto">
                    <div className="p-4">
                      <h3 className="text-white text-lg font-bold mb-4 text-center">VERSATILITY</h3>
                      
                      {/* 5 in a row - TOO CRAMPED */}
                      <div className="grid grid-cols-5 gap-1">
                        {['Hindi', 'English', 'Haryanvi', 'Punjabi', 'Pahadi'].map((lang, i) => (
                          <div key={i} className="bg-[#1a1a1a] rounded p-1 text-center">
                            <div className="aspect-[3/4] bg-gray-800 rounded mb-1"></div>
                            <p className="text-white text-[8px] truncate">{lang}</p>
                          </div>
                        ))}
                      </div>
                      
                      <div className="mt-4 p-3 bg-yellow-900/20 border border-yellow-600 rounded">
                        <p className="text-yellow-400 text-xs">⚠️ Cards too small, text unreadable</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* AFTER - Option 1: Vertical Stack */}
              <div>
                <div className="bg-green-900/20 border-2 border-green-500 rounded-lg p-4 mb-4">
                  <h3 className="text-xl font-bold text-green-400 mb-2">✅ OPTION 1: Vertical Stack</h3>
                  <p className="text-sm text-gray-400">Full cards stacked vertically</p>
                </div>
                
                <div className="bg-gray-900 rounded-3xl p-4 mx-auto" style={{ maxWidth: '375px' }}>
                  <div className="bg-[#0a0a0a] rounded-2xl overflow-hidden h-[600px] overflow-y-auto">
                    <div className="p-4 space-y-3">
                      <h3 className="text-white text-lg font-bold mb-4 text-center">VERSATILITY</h3>
                      
                      {['Hindi', 'English', 'Haryanvi'].map((lang, i) => (
                        <div key={i} className="bg-[#1a1a1a] rounded-lg overflow-hidden">
                          <div className="aspect-[16/9] bg-gray-800"></div>
                          <div className="p-3">
                            <span className="inline-block px-3 py-1 bg-[#FF6B6B] text-white text-xs font-bold rounded-full mb-2">
                              {lang}
                            </span>
                            <p className="text-gray-400 text-xs">Performance showcasing regional dialect...</p>
                          </div>
                        </div>
                      ))}
                      
                      <div className="p-2 bg-green-900/20 border border-green-600 rounded">
                        <p className="text-green-400 text-xs">✓ Easy to scroll, all content visible</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* AFTER - Option 2: Horizontal Carousel */}
            <div className="mt-8">
              <div className="bg-green-900/20 border-2 border-green-500 rounded-lg p-4 mb-4">
                <h3 className="text-xl font-bold text-green-400 mb-2">✅ OPTION 2: Horizontal Carousel</h3>
                <p className="text-sm text-gray-400">Swipeable cards, Instagram-style</p>
              </div>
              
              <div className="bg-gray-900 rounded-3xl p-4 mx-auto" style={{ maxWidth: '375px' }}>
                <div className="bg-[#0a0a0a] rounded-2xl overflow-hidden h-[500px]">
                  <div className="p-4">
                    <h3 className="text-white text-lg font-bold mb-4 text-center">VERSATILITY</h3>
                    
                    {/* Carousel Card */}
                    <div className="relative">
                      <div className="bg-[#1a1a1a] rounded-lg overflow-hidden">
                        <div className="aspect-[3/4] bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
                          <div className="text-center">
                            <h4 className="text-white text-3xl font-black mb-2">HINDI</h4>
                            <p className="text-white text-sm">Lead Role</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Dots */}
                      <div className="flex justify-center gap-2 mt-4">
                        <div className="w-2 h-2 rounded-full bg-[#FF6B6B]"></div>
                        <div className="w-2 h-2 rounded-full bg-gray-700"></div>
                        <div className="w-2 h-2 rounded-full bg-gray-700"></div>
                        <div className="w-2 h-2 rounded-full bg-gray-700"></div>
                        <div className="w-2 h-2 rounded-full bg-gray-700"></div>
                      </div>
                      
                      <div className="mt-4 p-3 bg-green-900/20 border border-green-600 rounded">
                        <p className="text-green-400 text-xs">✓ Swipe friendly, one focus at a time</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Demo 2: Portrait Gallery */}
        {activeDemo === 'portraits' && (
          <div className="space-y-12">
            <h2 className="text-3xl font-bold text-white text-center mb-8">
              Portrait Gallery - Mobile View
            </h2>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* BEFORE */}
              <div>
                <div className="bg-red-900/20 border-2 border-red-500 rounded-lg p-4 mb-4">
                  <h3 className="text-xl font-bold text-red-400 mb-2">❌ CURRENT (With Blurred Sides)</h3>
                  <p className="text-sm text-gray-400">Wasted space with blurred adjacent images</p>
                </div>
                
                <div className="bg-gray-900 rounded-3xl p-4 mx-auto" style={{ maxWidth: '375px' }}>
                  <div className="bg-[#0a0a0a] rounded-2xl overflow-hidden h-[600px]">
                    <div className="p-4 h-full flex flex-col justify-center">
                      <div className="relative flex items-center gap-2">
                        {/* Left blurred - TOO SMALL */}
                        <div className="w-16 opacity-20 blur-sm">
                          <div className="aspect-[3/4] bg-gray-700 rounded"></div>
                        </div>
                        
                        {/* Center */}
                        <div className="flex-1 relative">
                          <div className="aspect-[3/4] bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg"></div>
                          <div className="absolute top-2 right-2">
                            <span className="bg-black/60 text-white text-xs px-2 py-1 rounded-full">5 / 11</span>
                          </div>
                        </div>
                        
                        {/* Right blurred - TOO SMALL */}
                        <div className="w-16 opacity-20 blur-sm">
                          <div className="aspect-[3/4] bg-gray-700 rounded"></div>
                        </div>
                        
                        {/* Arrows */}
                        <button className="absolute left-1 w-8 h-8 bg-black/60 rounded-full flex items-center justify-center">
                          <ChevronLeft size={16} className="text-white" />
                        </button>
                        <button className="absolute right-1 w-8 h-8 bg-black/60 rounded-full flex items-center justify-center">
                          <ChevronRight size={16} className="text-white" />
                        </button>
                      </div>
                      
                      <div className="mt-4 p-3 bg-yellow-900/20 border border-yellow-600 rounded">
                        <p className="text-yellow-400 text-xs">⚠️ Blurred sides waste space, arrows too small</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* AFTER */}
              <div>
                <div className="bg-green-900/20 border-2 border-green-500 rounded-lg p-4 mb-4">
                  <h3 className="text-xl font-bold text-green-400 mb-2">✅ OPTIMIZED (Full Width)</h3>
                  <p className="text-sm text-gray-400">Larger image, bigger arrows, better UX</p>
                </div>
                
                <div className="bg-gray-900 rounded-3xl p-4 mx-auto" style={{ maxWidth: '375px' }}>
                  <div className="bg-[#0a0a0a] rounded-2xl overflow-hidden h-[600px]">
                    <div className="p-4 h-full flex flex-col justify-center">
                      <div className="relative">
                        {/* Full width image */}
                        <div className="aspect-[3/4] bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg"></div>
                        
                        {/* Counter at bottom */}
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2">
                          <span className="bg-black/70 backdrop-blur-sm text-white text-sm px-4 py-2 rounded-full border border-white/30">
                            5 / 11
                          </span>
                        </div>
                        
                        {/* Bigger arrows */}
                        <button className="absolute left-3 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/70 backdrop-blur-sm rounded-full flex items-center justify-center">
                          <ChevronLeft size={24} className="text-white" />
                        </button>
                        <button className="absolute right-3 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/70 backdrop-blur-sm rounded-full flex items-center justify-center">
                          <ChevronRight size={24} className="text-white" />
                        </button>
                      </div>
                      
                      {/* Dots below */}
                      <div className="flex justify-center gap-1.5 mt-4 overflow-x-auto">
                        {[...Array(11)].map((_, i) => (
                          <div 
                            key={i}
                            className={`h-1.5 rounded-full ${i === 4 ? 'bg-[#4ECDC4] w-6' : 'bg-gray-700 w-1.5'}`}
                          ></div>
                        ))}
                      </div>
                      
                      <div className="mt-4 p-3 bg-green-900/20 border border-green-600 rounded">
                        <p className="text-green-400 text-xs">✓ Larger image, easier navigation, better visibility</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Demo 3: CTA Buttons */}
        {activeDemo === 'cta' && (
          <div className="space-y-12">
            <h2 className="text-3xl font-bold text-white text-center mb-8">
              Landing CTA Buttons - Mobile View
            </h2>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* BEFORE */}
              <div>
                <div className="bg-red-900/20 border-2 border-red-500 rounded-lg p-4 mb-4">
                  <h3 className="text-xl font-bold text-red-400 mb-2">❌ CURRENT (Horizontal)</h3>
                  <p className="text-sm text-gray-400">3 buttons cramped in one row</p>
                </div>
                
                <div className="bg-gray-900 rounded-3xl p-4 mx-auto" style={{ maxWidth: '375px' }}>
                  <div className="bg-[#0a0a0a] rounded-2xl overflow-hidden h-[600px]">
                    <div className="p-4 h-full flex flex-col justify-center items-center">
                      <h1 className="text-white text-4xl font-black mb-6 text-center">KOMAL VERMA</h1>
                      
                      {/* Cramped horizontal buttons */}
                      <div className="flex gap-2 flex-wrap justify-center">
                        <button className="px-4 py-2 bg-[#FF6B6B] text-white text-xs rounded flex items-center gap-1">
                          <Play size={12} />
                          Watch
                        </button>
                        <button className="px-4 py-2 bg-[#4ECDC4] text-white text-xs rounded">
                          Portraits
                        </button>
                        <button className="px-4 py-2 border border-white text-white text-xs rounded">
                          Explore
                        </button>
                      </div>
                      
                      <div className="mt-6 p-3 bg-yellow-900/20 border border-yellow-600 rounded max-w-xs">
                        <p className="text-yellow-400 text-xs">⚠️ Buttons too small, text truncated, hard to tap</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* AFTER */}
              <div>
                <div className="bg-green-900/20 border-2 border-green-500 rounded-lg p-4 mb-4">
                  <h3 className="text-xl font-bold text-green-400 mb-2">✅ OPTIMIZED (Vertical Stack)</h3>
                  <p className="text-sm text-gray-400">Full-width buttons, easy to tap</p>
                </div>
                
                <div className="bg-gray-900 rounded-3xl p-4 mx-auto" style={{ maxWidth: '375px' }}>
                  <div className="bg-[#0a0a0a] rounded-2xl overflow-hidden h-[600px]">
                    <div className="p-6 h-full flex flex-col justify-center">
                      <h1 className="text-white text-3xl font-black mb-8 text-center">KOMAL VERMA</h1>
                      
                      {/* Stacked full-width buttons */}
                      <div className="space-y-4 w-full">
                        <button className="w-full px-6 py-4 bg-[#FF6B6B] text-white font-bold rounded flex items-center justify-center gap-2">
                          <Play size={20} />
                          Watch Showreel
                        </button>
                        <button className="w-full px-6 py-4 bg-[#4ECDC4] text-white font-bold rounded">
                          View Portraits
                        </button>
                        <button className="w-full px-6 py-4 border-2 border-white text-white font-bold rounded">
                          Explore My Work
                        </button>
                      </div>
                      
                      <div className="mt-8 p-3 bg-green-900/20 border border-green-600 rounded">
                        <p className="text-green-400 text-xs">✓ Larger tap targets, clear labels, easy to use</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Summary */}
        <div className="mt-16 bg-[#1a1a1a] rounded-lg p-8 border border-gray-800">
          <h3 className="text-2xl font-bold text-white mb-4">Implementation Summary</h3>
          <div className="space-y-4 text-gray-300">
            <div className="flex items-start gap-3">
              <span className="text-[#FF6B6B] text-xl">1.</span>
              <div>
                <strong className="text-white">Versatility Section:</strong> Choose between vertical stack or horizontal carousel for mobile
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-[#4ECDC4] text-xl">2.</span>
              <div>
                <strong className="text-white">Portrait Gallery:</strong> Remove blurred sides on mobile, use full-width images with bigger navigation
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-[#FFD700] text-xl">3.</span>
              <div>
                <strong className="text-white">CTA Buttons:</strong> Stack vertically on mobile for better tap targets and readability
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileMockups;
